import { BreadcrumbsProps } from '@approvalmax/ui/src/components';
import { useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { State } from 'modules/data';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    breadcrumbsHeaderState,
    useAllCompaniesLinksItems,
    useRequestsLinkItems,
    useWorkflowsLinkItems,
} from 'shared/components';
import { getPath, Path } from 'urlBuilder';

import { isTemplateRequestListFilter } from '../config';
import { messages } from '../messages';
import { RequestListPage } from '../types';

export const useRequestsListBreadcrumbs = (requestListPage: RequestListPage) => {
    const setBreadcrumbs = useSetAtom(breadcrumbsHeaderState);
    const { section, companyId, workflowId, status } = useParams<{
        section?: string;
        companyId?: string;
        workflowId?: string;
        status?: string;
    }>();
    const allCompaniesLinksItems = useAllCompaniesLinksItems();
    const company = useSelector((state: State) =>
        companyId ? selectors.company.findCompanyById(state, companyId) : null
    );
    const requestsLinkItems = useRequestsLinkItems(company);
    const workflowsLinkItems = useWorkflowsLinkItems(company);

    const breadcrumbs: BreadcrumbsProps['items'] = useMemo(() => {
        switch (requestListPage) {
            case 'search':
                return [
                    {
                        name: messages.searchResultsPageName,
                        to: getPath(Path.searchRequests),
                    },
                ];

            case 'my': {
                const currentSection = allCompaniesLinksItems.find(({ id }) => id === section);

                return currentSection ? [currentSection] : [];
            }

            case 'company': {
                const currentSection = requestsLinkItems.find(({ id }) => id === section);

                return currentSection ? [currentSection] : [];
            }

            case 'workflow': {
                const currentItem = workflowsLinkItems.find((item) => item.workflowId === workflowId);

                if (!currentItem) return [];

                const result: BreadcrumbsProps['items'] = [
                    {
                        name: currentItem.name,
                        to: currentItem.subItems[0].to,
                    },
                ];

                if (companyId && workflowId && status && isTemplateRequestListFilter(status)) {
                    result.push({
                        value: getPath(Path.requestTemplateList, companyId, workflowId, status) || '',
                        subItems: currentItem.subItems.filter(({ available }) => available ?? true),
                    });
                }

                return result;
            }

            default:
                return [];
        }
    }, [
        requestListPage,
        allCompaniesLinksItems,
        section,
        requestsLinkItems,
        workflowsLinkItems,
        companyId,
        workflowId,
        status,
    ]);

    useEffect(() => {
        setBreadcrumbs(breadcrumbs);
    }, [setBreadcrumbs, breadcrumbs]);
};
